/**
 * @file Verify Code Page — /verify-code
 *
 * 6-digit code verification (email magic code or WhatsApp).
 */
import React, { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { authService } from "../services/auth";
import { useAuth } from "../hooks/useAuth";
import { AUTH_STRINGS as S } from "../constants/strings";
import AuthShell from "../components/AuthShell";
import { Button, toast } from "../ui";

export default function VerifyCode() {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleAuthSuccess } = useAuth();

  const email = location.state?.email;
  const phone = location.state?.phone;
  const identifier = email || phone;

  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);

  if (!identifier) return <Navigate to="/login" replace />;

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setIsSubmitting(true);
    setError("");
    try {
      const response = phone
        ? await authService.verifyPhoneCode(phone, code)
        : await authService.verifyCode(email, code);
      handleAuthSuccess(response.data);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.message || "Código inválido");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    try {
      if (phone) await authService.requestPhoneCode(phone);
      else await authService.requestCode(email);
      toast.success(S.VERIFY_RESEND_SUCCESS);
    } catch {
      toast.error("Error al reenviar el código");
    } finally {
      setIsResending(false);
    }
  };

  const subtitle = phone
    ? "Enviamos un código de 6 dígitos a tu WhatsApp"
    : S.VERIFY_SUBTITLE;

  return (
    <AuthShell title={S.VERIFY_TITLE} subtitle={`${subtitle} ${identifier}`}>
      {error && (
        <div className="auth-alert auth-alert-error">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
      <form onSubmit={handleVerify} className="auth-form">
        <input
          className="auth-code-input"
          inputMode="numeric"
          autoFocus
          maxLength={6}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          aria-label={S.VERIFY_CODE_LABEL}
          placeholder="000000"
        />
        <Button type="submit" size="lg" fullWidth loading={isSubmitting} disabled={code.length !== 6}>
          {S.VERIFY_SUBMIT}
        </Button>
        <Button variant="ghost" type="button" onClick={handleResend} loading={isResending} fullWidth>
          {S.VERIFY_RESEND}
        </Button>
      </form>
    </AuthShell>
  );
}
